import { StripeCardForm } from './StripeCardForm';
import { BraintreeCardForm } from './BraintreeCardForm';
import { CheckoutContext } from '@/context/CheckoutContext';
import { PaymentMethodEnum, ProviderEnum } from '@/types/common';
import { useContext, useEffect, useMemo, useRef } from 'react';
import { trackCardFormReadyToPay } from '@/utils/conversionTracking';
import { SaveCardCheckbox } from '../PaymentInputs/SaveCardCheckbox';
import { AdyenCardForm } from './AdyenCardForm';
import { NuveiCardForm } from './NuveiCardForm';
import { ChargebeeCardForm } from './ChargebeeCardForm';
import { PayButton } from '../PaymentInputs/PayButton';
import { AppchargePaymentElementForm } from './AppchargePaymentElementForm';
import PaymentMethodNotSupported from '@/components/PaymentMethod/PaymentMethodNotSupported';
import { useIsPaymentMethodUnavailable } from '@/context/PaymentMethodAvailabilityContext';
import { SubscriptionDisclaimer } from '../Subscriptions/SubscriptionDisclaimer/SubscriptionDisclaimer';

export const InitialCreditCardForm = () => {
  const context = useContext(CheckoutContext);
  const {
    paymentMethodsSettings,
    card: { initialCardPayload, setInitialCardPayload },
    checkoutConfiguration,
    payment,
    actions: { runPayment },
  } = context;
  const cardSettings = paymentMethodsSettings.find((pm) => pm.paymentMethodName === PaymentMethodEnum.CREDIT_CARD);
  const isUnavailable = useIsPaymentMethodUnavailable();

  // Latest context kept in a ref so the readiness effect can fire-and-forget
  // without taking `context` as a dependency (it changes identity every render).
  const contextRef = useRef(context);
  contextRef.current = context;

  // First time the manual card form reaches a payable (valid) state, i.e. the
  // Pay button becomes enabled. Provider-agnostic: every PSP card form feeds
  // `payment.isFormValid`. Lets us split "never completed the form" from
  // "completed but didn't tap Pay" in the present → pay drop-off. The tracker
  // is session-once, so this only meaningfully runs on the first valid edge.
  useEffect(() => {
    if (payment.isFormValid) {
      trackCardFormReadyToPay(contextRef.current);
    }
  }, [payment.isFormValid]);

  const Component = useMemo(() => {
    if (!cardSettings) return null;

    if (checkoutConfiguration?.isRawPanSelfManaged) {
      return <AppchargePaymentElementForm />;
    }

    if (cardSettings.provider === ProviderEnum.STRIPE) {
      return <StripeCardForm />;
    }

    if (cardSettings.provider === ProviderEnum.BRAINTREE) {
      return <BraintreeCardForm />;
    }
    if (cardSettings.provider === ProviderEnum.ADYEN) {
      return <AdyenCardForm />;
    }
    if (cardSettings.provider === ProviderEnum.NUVEI) {
      return <NuveiCardForm />;
    }
    if (cardSettings.provider === ProviderEnum.CHARGEBEE) {
      return <ChargebeeCardForm />;
    }

    return null;
  }, [cardSettings, checkoutConfiguration?.isRawPanSelfManaged]);

  const handleSaveCardChange = (value: boolean) => {
    setInitialCardPayload({ ...initialCardPayload, isSaveCard: value });
  };

  const handlePayment = () => {
    runPayment({
      newCard: true,
      isNewPaymentMethodSaved: initialCardPayload?.isSaveCard,
      isSavedPaymentMethodUsed: false,
    });
  };

  return (
    <div data-testid="initial-credit-card-form" className="flex flex-col gap-4">
      {Component ?? <PaymentMethodNotSupported />}
      {Component && !isUnavailable && (
        <>
          {checkoutConfiguration?.showSaveMyCard && (
            <div className="flex items-center w-full gap-2">
              <SaveCardCheckbox
                isSaveCard={initialCardPayload?.isSaveCard ?? false}
                setIsSaveCard={handleSaveCardChange}
              />
            </div>
          )}
          {/* Subscriptions only (renders null otherwise): the ROSCA/FTC
              renewal disclosure must sit directly above the CTA, per method
              (matches v2/Figma) — see the wallet forms for the Apple/Google
              Pay equivalent. */}
          <SubscriptionDisclaimer />
          <div className="flex flex-col">
            <PayButton isDisabled={!payment.isFormValid} onClick={handlePayment} />
          </div>
        </>
      )}
    </div>
  );
};
